import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Plus, Pencil, Trash2, Award, Info, Database, FileText } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const SecuredAssetsReleasePage = () => {
    const [loading, setLoading] = useState(true);

    // Release Settings State
    const [releaseSettings, setReleaseSettings] = useState({
        section_tag: 'Successful Completion',
        section_title: 'When Your Loan Is Fully Repaid',
        section_intro: '',
        footer_heading: '',
        footer_text: ''
    });

    // Release Steps State
    const [releaseSteps, setReleaseSteps] = useState([]);
    const [isStepModalOpen, setIsStepModalOpen] = useState(false);
    const [stepModalData, setStepModalData] = useState({
        step_number: '1',
        title: '',
        description: '',
        display_order: 0,
        is_active: true
    });
    const [editingStep, setEditingStep] = useState(null);
    const [stepDeleteConfirm, setStepDeleteConfirm] = useState({ isOpen: false, id: null });

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsRes, stepsRes] = await Promise.all([
                axios.get('/api/admin/secured-assets/release-settings'),
                axios.get('/api/admin/secured-assets/release-steps')
            ]);

            if (settingsRes.data.success && settingsRes.data.data) {
                setReleaseSettings(settingsRes.data.data);
            }
            if (stepsRes.data.success) {
                setReleaseSteps(stepsRes.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSettingsChange = (e) => {
        const { name, value } = e.target;
        setReleaseSettings(prev => ({ ...prev, [name]: value }));
    };

    const handleSettingsSubmit = async () => {
        try {
            await axios.put('/api/admin/secured-assets/release-settings', releaseSettings);
            toast.success('Settings updated');
            fetchData();
        } catch (error) {
            console.error('Error updating settings:', error);
            toast.error('Failed to update settings');
        }
    };

    const handleAddStep = () => {
        setEditingStep(null);
        setStepModalData({
            step_number: `${releaseSteps.length + 1}`,
            title: '',
            description: '',
            display_order: (releaseSteps.length + 1),
            is_active: true
        });
        setIsStepModalOpen(true);
    };

    const handleEditStep = (step) => {
        setEditingStep(step);
        setStepModalData({
            ...step,
            is_active: Boolean(step.is_active)
        });
        setIsStepModalOpen(true);
    };

    const handleStepSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingStep) {
                await axios.put(`/api/admin/secured-assets/release-steps/${editingStep.id}`, stepModalData);
                toast.success('Step updated');
            } else {
                await axios.post('/api/admin/secured-assets/release-steps', stepModalData);
                toast.success('Step created');
            }
            setIsStepModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving step:', error);
            toast.error('Failed to save step');
        }
    };

    const confirmDeleteStep = async () => {
        try {
            await axios.delete(`/api/admin/secured-assets/release-steps/${stepDeleteConfirm.id}`);
            toast.success('Step deleted');
            setStepDeleteConfirm({ isOpen: false, id: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting step:', error);
            toast.error('Failed to delete step');
        }
    };

    if (loading) return <div className="p-8 text-center text-slate-500">Loading...</div>;

    return (
        <div className="max-w-7xl mx-auto pb-12">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Secured Assets - Successful Completion</h1>
                    <p className="text-slate-500 mt-1">Manage release process steps and success information</p>
                </div>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-8">
                <div className="flex items-center gap-2 mb-6 text-primary-900 border-b border-slate-50 pb-4">
                    <Info size={20} className="font-bold" />
                    <h2 className="text-lg font-bold">Main Section Settings</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Tag</label>
                        <input
                            type="text"
                            name="section_tag"
                            value={releaseSettings.section_tag || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                        <input
                            type="text"
                            name="section_title"
                            value={releaseSettings.section_title || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Intro Text</label>
                        <textarea
                            name="section_intro"
                            value={releaseSettings.section_intro || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all h-24 resize-none"
                        />
                    </div>
                </div>

                <div className="flex items-center gap-2 mb-6 text-primary-900 border-b border-slate-50 pb-4">
                    <Award size={20} className="font-bold" />
                    <h2 className="text-lg font-bold">Footer Card (Your Achievement)</h2>
                </div>

                <div className="grid grid-cols-1 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Footer Heading</label>
                        <input
                            type="text"
                            name="footer_heading"
                            value={releaseSettings.footer_heading || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                            placeholder="e.g., Your Achievement, Our Shared Success"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Footer Description</label>
                        <textarea
                            name="footer_text"
                            value={releaseSettings.footer_text || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all h-24 resize-none"
                            placeholder="Success card main text..."
                        />
                    </div>
                </div>

                <div className="flex justify-end">
                    <button
                        onClick={handleSettingsSubmit}
                        className="px-6 py-2.5 bg-primary-900 text-white rounded-xl font-semibold hover:bg-primary-800 transition-all shadow-md"
                    >
                        Save Settings
                    </button>
                </div>
            </div>

            {/* Process Steps */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <div className="flex justify-between items-center mb-6 border-b border-slate-50 pb-4">
                    <div className="flex items-center gap-2 text-primary-900">
                        <Award size={20} className="font-bold" />
                        <h2 className="text-lg font-bold">Release Process Steps</h2>
                    </div>
                    <button
                        onClick={handleAddStep}
                        className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl hover:bg-primary-700 transition-colors shadow-md font-semibold"
                    >
                        <Plus size={18} /> Add Step
                    </button>
                </div>

                <div className="space-y-4">
                    {releaseSteps.map((step) => (
                        <div key={step.id} className="bg-slate-50 rounded-xl p-4 border border-slate-100 flex items-start gap-4 group">
                            <div className="w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-primary-600 font-bold text-lg flex-shrink-0 shadow-sm">
                                {step.step_number}
                            </div>
                            <div className="flex-grow">
                                <h3 className="text-slate-800 font-bold">{step.title}</h3>
                                <p className="text-slate-600 text-sm mt-1">{step.description}</p>
                                <div className="flex items-center gap-3 mt-2">
                                    <span className="text-xs text-slate-400">Order: {step.display_order}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${step.is_active ? 'bg-green-50 text-green-600' : 'bg-slate-200 text-slate-500'}`}>
                                        {step.is_active ? 'Active' : 'Hidden'}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => handleEditStep(step)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Pencil size={18} /></button>
                                <button onClick={() => setStepDeleteConfirm({ isOpen: true, id: step.id })} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={18} /></button>
                            </div>
                        </div>
                    ))}
                    {releaseSteps.length === 0 && (
                        <div className="text-center py-8 text-slate-400">No steps found. Add your first step!</div>
                    )}
                </div>
            </div>

            {/* Step Modal */}
            {isStepModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6 animate-in zoom-in duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-slate-800">{editingStep ? 'Edit Process Step' : 'Add New Step'}</h2>
                            <button onClick={() => setIsStepModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <Plus className="rotate-45" size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleStepSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Step Number</label>
                                <input
                                    type="text"
                                    value={stepModalData.step_number}
                                    onChange={(e) => setStepModalData({ ...stepModalData, step_number: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                    placeholder="1, 2..."
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Title</label>
                                <input
                                    type="text"
                                    value={stepModalData.title}
                                    onChange={(e) => setStepModalData({ ...stepModalData, title: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Description</label>
                                <textarea
                                    value={stepModalData.description}
                                    onChange={(e) => setStepModalData({ ...stepModalData, description: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none h-24 resize-none"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Display Order</label>
                                    <input
                                        type="number"
                                        value={stepModalData.display_order}
                                        onChange={(e) => setStepModalData({ ...stepModalData, display_order: parseInt(e.target.value) })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Status</label>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={stepModalData.is_active}
                                            onChange={(e) => setStepModalData({ ...stepModalData, is_active: e.target.checked })}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                        <span className="ml-3 text-sm font-medium text-slate-700">{stepModalData.is_active ? 'Active' : 'Hidden'}</span>
                                    </label>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                                <button type="button" onClick={() => setIsStepModalOpen(false)} className="px-6 py-2.5 border border-slate-300 text-slate-600 rounded-xl font-semibold hover:bg-slate-50 transition-all">Cancel</button>
                                <button type="submit" className="px-6 py-2.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all shadow-lg">{editingStep ? 'Update Step' : 'Create Step'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={stepDeleteConfirm.isOpen}
                onClose={() => setStepDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDeleteStep}
                title="Delete Step"
                message="Are you sure you want to delete this process step? This action cannot be undone."
            />
        </div>
    );
};

export default SecuredAssetsReleasePage;
